import { Suspense } from 'react';
import { QueryErrorBoundary } from '@sara/ui/components/query-error-boundary';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  getGetRubricQueryOptions,
  getGetRubricQueryKey,
  getListRubricsQueryKey,
  useCreateRubricVersion,
} from '@sara/api-client';
import { RubricForm, RubricFormSkeleton, type RubricFormValues } from '../../components/RubricForm';
import { rubricToFormValues } from '../../components/RubricForm/mapping';
import { toSuspense } from '../../../../lib/query';
import { useOrgSlug } from '../../../auth';

interface CreateRubricVersionFormContainerProps {
  rubricId: string;
}

function CreateRubricVersionFormContainerContent({
  rubricId,
}: CreateRubricVersionFormContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgSlug = useOrgSlug();

  const { data: rubric } = useSuspenseQuery(toSuspense(getGetRubricQueryOptions(rubricId)));

  const defaultValues: RubricFormValues | undefined = rubricToFormValues(rubric);

  const { mutate, isPending, error } = useCreateRubricVersion({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetRubricQueryKey(rubricId),
        });
        void queryClient.invalidateQueries({
          queryKey: getListRubricsQueryKey(),
        });
        void navigate({
          to: '/orgs/$orgSlug/rubrics/$rubricId',
          params: { orgSlug, rubricId },
        });
      },
    },
  });

  function handleSubmit(values: RubricFormValues) {
    mutate({
      id: rubricId,
      data: {
        dimensions: values.dimensions,
        bands: values.bands,
        flags: values.flags,
      },
    });
  }

  return (
    <RubricForm
      mode="newVersion"
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isPending={isPending}
      error={error ? (error instanceof Error ? error.message : 'Failed to create version') : null}
    />
  );
}

export function CreateRubricVersionFormContainer({
  rubricId,
}: CreateRubricVersionFormContainerProps) {
  return (
    <QueryErrorBoundary>
      <Suspense fallback={<RubricFormSkeleton />}>
        <CreateRubricVersionFormContainerContent rubricId={rubricId} />
      </Suspense>
    </QueryErrorBoundary>
  );
}
